import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  CheckCircle, WarningCircle, XCircle, CaretDown, CaretRight,
  PaperPlaneRight, ShieldCheck,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  dati_cliente: 'Dati cliente',
  commerciale: 'Area commerciale',
  compliance: 'Compliance ARERA',
  gdpr: 'GDPR',
  tecnico: 'Dati tecnici',
};

const STATUS_ICON = {
  ok: { Icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  warning: { Icon: WarningCircle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  error: { Icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
};

export default function ValidationGateDialog({
  open, onOpenChange, leadId, leadName, onConfirmSend, sendLabel = 'Invia comunque',
}) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !leadId) return;
    setLoading(true);
    fetch(`${API}/api/leads/${leadId}/validation-gate`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setReport(d))
      .finally(() => setLoading(false));
  }, [open, leadId]);

  const grouped = (report?.checks || []).reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});

  const canSend = report?.can_send;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="validation-gate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={20} weight="duotone" className="text-orange-500" />
            Validation Gate — {leadName || '...'}
          </DialogTitle>
          <DialogDescription>
            Checklist obbligatoria prima dell'invio del precontratto.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-500">Caricamento controlli...</div>
        ) : !report ? (
          <div className="py-12 text-center text-red-500">Errore caricamento</div>
        ) : (
          <>
            <div className={`rounded-lg p-4 ${
              canSend && report.severity === 'ok' ? 'bg-green-50 border border-green-200' :
              canSend ? 'bg-yellow-50 border border-yellow-200' :
              'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-3">
                {canSend && report.severity === 'ok' ? (
                  <><CheckCircle size={28} weight="fill" className="text-green-600" />
                    <div>
                      <div className="font-semibold text-green-800">Tutti i controlli superati</div>
                      <div className="text-xs text-green-700">Il precontratto può essere inviato</div>
                    </div></>
                ) : canSend ? (
                  <><WarningCircle size={28} weight="fill" className="text-yellow-600" />
                    <div>
                      <div className="font-semibold text-yellow-800">Invio possibile con avvisi</div>
                      <div className="text-xs text-yellow-700">
                        {report.summary.warning} avviso/i — l'invio non è bloccato ma valuta se integrare prima
                      </div>
                    </div></>
                ) : (
                  <><XCircle size={28} weight="fill" className="text-red-600" />
                    <div>
                      <div className="font-semibold text-red-800">Invio bloccato</div>
                      <div className="text-xs text-red-700">
                        {report.summary.blocking} errore/i critico/i da correggere prima di inviare
                      </div>
                    </div></>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <Badge className="bg-green-100 text-green-700">{report.summary.ok} OK</Badge>
                <Badge className="bg-yellow-100 text-yellow-700">{report.summary.warning} Avvisi</Badge>
                <Badge className="bg-red-100 text-red-700">{report.summary.error} Errori</Badge>
              </div>
            </div>

            <div className="space-y-3 mt-4">
              {Object.entries(grouped).map(([cat, items]) => (
                <CategoryBlock key={cat} category={cat} items={items} />
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vg-cancel">
                Annulla
              </Button>
              {canSend ? (
                <Button onClick={() => { onOpenChange(false); onConfirmSend?.(); }}
                        className="bg-orange-500 hover:bg-orange-600"
                        data-testid="vg-confirm-send">
                  <PaperPlaneRight size={14} className="mr-1" /> {sendLabel}
                </Button>
              ) : (
                <Button disabled className="opacity-50 cursor-not-allowed">
                  <XCircle size={14} className="mr-1" /> Invio bloccato
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


function CategoryBlock({ category, items }) {
  const [open, setOpen] = useState(true);
  const hasError = items.some(i => i.status === 'error');
  const hasWarn = items.some(i => i.status === 'warning');
  return (
    <div className="border rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)}
              className={`w-full flex items-center justify-between p-3 ${
                hasError ? 'bg-red-50' : hasWarn ? 'bg-yellow-50' : 'bg-green-50'
              } hover:brightness-95 transition`}>
        <div className="flex items-center gap-2 font-medium text-sm">
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
          {CATEGORY_LABELS[category] || category}
          <span className="text-[10px] text-slate-500">({items.length})</span>
        </div>
        <div className="flex gap-1">
          {items.filter(i => i.status === 'ok').length > 0 && (
            <Badge className="bg-green-100 text-green-700 text-[10px]">
              {items.filter(i => i.status === 'ok').length}
            </Badge>
          )}
          {items.filter(i => i.status === 'warning').length > 0 && (
            <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">
              {items.filter(i => i.status === 'warning').length}
            </Badge>
          )}
          {items.filter(i => i.status === 'error').length > 0 && (
            <Badge className="bg-red-100 text-red-700 text-[10px]">
              {items.filter(i => i.status === 'error').length}
            </Badge>
          )}
        </div>
      </button>
      {open && (
        <div className="divide-y">
          {items.map(item => {
            const s = STATUS_ICON[item.status] || STATUS_ICON.warning;
            return (
              <div key={item.id} className={`flex items-start gap-3 p-3 ${s.bg}/30`}
                   data-testid={`vg-check-${item.id}`}>
                <s.Icon size={18} className={`${s.color} shrink-0 mt-0.5`} weight="fill" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">{item.label}</div>
                    {item.blocking && item.status === 'error' && (
                      <Badge className="bg-red-600 text-white text-[9px]">BLOCKING</Badge>
                    )}
                  </div>
                  <div className={`text-xs mt-0.5 ${
                    item.status === 'error' ? 'text-red-700' :
                    item.status === 'warning' ? 'text-yellow-700' : 'text-slate-600'
                  }`}>{item.message}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Global cache of validation reports keyed by leadId — filled by PreContractsPage on load
const _VG_CACHE = new Map();
const _VG_SUBSCRIBERS = new Set();

export function primeValidationGateCache(reports) {
  reports.forEach(r => _VG_CACHE.set(r.lead_id, r));
  _VG_SUBSCRIBERS.forEach(fn => fn());
}

/**
 * Inline mini-badge for Lead/Precontract tables.
 * Reads from the shared cache if available (no fetch).
 * Falls back to single fetch if cache miss.
 */
export function ValidationGateBadge({ leadId }) {
  const [, forceRender] = useState(0);
  const [report, setReport] = useState(() => _VG_CACHE.get(leadId) || null);

  useEffect(() => {
    const cached = _VG_CACHE.get(leadId);
    if (cached) { setReport(cached); return; }
    let cancelled = false;
    // Subscribe for late-arriving batch results
    const sub = () => {
      const r = _VG_CACHE.get(leadId);
      if (r && !cancelled) setReport(r);
    };
    _VG_SUBSCRIBERS.add(sub);
    // Fallback: single fetch after 1.2s if no batch filled cache
    const t = setTimeout(() => {
      if (_VG_CACHE.has(leadId) || cancelled) return;
      fetch(`${API}/api/leads/${leadId}/validation-gate`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d && !cancelled) { _VG_CACHE.set(leadId, d); setReport(d); } });
    }, 1200);
    return () => { cancelled = true; _VG_SUBSCRIBERS.delete(sub); clearTimeout(t); forceRender(x => x + 1); };
  }, [leadId]);

  if (!report) return <span className="w-2 h-2 rounded-full bg-slate-200 inline-block" />;
  const color = report.severity === 'error' ? 'bg-red-500' :
                report.severity === 'warning' ? 'bg-yellow-500' : 'bg-green-500';
  const title = `${report.summary.ok} OK · ${report.summary.warning} avvisi · ${report.summary.error} errori`;
  return (
    <span className={`w-2 h-2 rounded-full inline-block ${color}`}
          title={title}
          data-testid={`vg-badge-${leadId}`} />
  );
}
